import { useEffect, useRef } from 'react';
import axios from 'axios';
import { COINS } from '../constants';

/**
 * usePrices — Real-time price streaming via Server-Sent Events (SSE).
 *
 * Connects to /api/stream/prices (SSE endpoint on proxy-server.js).
 * The server maintains a WebSocket to Binance Global (50-200+ trades/sec
 * for BTC) and relays every trade to the browser via SSE.
 *
 * Features:
 *   - True global Binance feed (server-side WS, no geo-blocking)
 *   - EventSource auto-reconnect (built into browser API)
 *   - Falls back to REST polling if SSE fails
 *   - Throttled price history updates (every 3s) to avoid flooding React
 *   - Price direction tracking (up/down/flat) for UI flash animations
 *
 * @param {Function} setPrices - State setter for live prices
 * @param {Function} setPriceHistory - State setter for price history (graph points)
 */

const HISTORY_THROTTLE_MS = 3000;

const usePrices = (setPrices, setPriceHistory) => {
    const lastHistoryUpdate = useRef(0);
    const latestPricesRef = useRef({});
    const fallbackIntervalRef = useRef(null);
    const isMountedRef = useRef(true);
    const eventSourceRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;

        const SYMBOL_MAP = {
            btcusdt: 'bitcoin',
            ethusdt: 'ethereum',
            solusdt: 'solana',
        };

        // --- SSE connection to server-side Binance relay ---
        const connectSSE = () => {
            if (!isMountedRef.current) return;

            try {
                const es = new EventSource('/api/stream/prices');
                eventSourceRef.current = es;

                es.onopen = () => {
                    console.log('[SSE] Connected to trade stream — live prices active');
                    // Stop REST fallback if running
                    if (fallbackIntervalRef.current) {
                        clearInterval(fallbackIntervalRef.current);
                        fallbackIntervalRef.current = null;
                        console.log('[SSE] REST fallback stopped — SSE active');
                    }
                };

                es.onmessage = (event) => {
                    if (!isMountedRef.current) return;
                    try {
                        const { coinId, price, direction } = JSON.parse(event.data);
                        if (!coinId || !price) return;

                        // Update live price immediately
                        latestPricesRef.current[coinId] = price;
                        setPrices(prev => {
                            const old = prev[coinId];
                            if (old?.usd === price) return prev;
                            return {
                                ...prev,
                                [coinId]: {
                                    usd: price,
                                    prevUsd: old?.usd || price,
                                    direction: direction || 'flat',
                                    tick: Date.now(),
                                },
                            };
                        });

                        // Throttled history update (every 3s)
                        const now = Date.now();
                        if (now - lastHistoryUpdate.current >= HISTORY_THROTTLE_MS) {
                            lastHistoryUpdate.current = now;
                            const snapshot = { ...latestPricesRef.current };
                            setPriceHistory(prev => {
                                const next = { ...prev };
                                COINS.forEach(coin => {
                                    const p = snapshot[coin.id];
                                    if (p && p > 0) {
                                        next[coin.id] = [
                                            ...(next[coin.id] || []),
                                            { time: now, price: p },
                                        ].slice(-60);
                                    }
                                });
                                return next;
                            });
                        }
                    } catch { /* ignore malformed */ }
                };

                es.onerror = () => {
                    console.warn('[SSE] Connection error — EventSource will auto-reconnect');
                    // EventSource auto-reconnects, but if it fails 3 times rapidly,
                    // switch to REST fallback
                    if (es.readyState === EventSource.CLOSED) {
                        console.warn('[SSE] Connection closed permanently — switching to REST fallback');
                        startRestFallback();
                    }
                };
            } catch (err) {
                console.error('[SSE] Failed to create EventSource:', err.message);
                startRestFallback();
            }
        };

        // --- REST fallback (used only if SSE fails) ---
        const startRestFallback = () => {
            if (fallbackIntervalRef.current) return;
            console.log('[SSE] REST fallback active — polling CoinGecko every 2s');

            const fetchPrices = async () => {
                if (!isMountedRef.current) return;
                try {
                    const response = await axios.get('https://api.coingecko.com/api/v3/simple/price', {
                        params: {
                            ids: 'bitcoin,ethereum,solana',
                            vs_currencies: 'usd',
                        },
                        timeout: 5000,
                    });

                    const newData = {};
                    for (const [coinId, data] of Object.entries(response.data)) {
                        if (data.usd) {
                            const price = data.usd;
                            newData[coinId] = {
                                usd: price,
                                prevUsd: latestPricesRef.current[coinId] || price,
                                direction: !latestPricesRef.current[coinId] ? 'flat'
                                    : price > latestPricesRef.current[coinId] ? 'up'
                                        : price < latestPricesRef.current[coinId] ? 'down' : 'flat',
                                tick: Date.now(),
                            };
                            latestPricesRef.current[coinId] = price;
                        }
                    }

                    if (Object.keys(newData).length > 0) {
                        setPrices(prev => ({ ...prev, ...newData }));

                        const now = Date.now();
                        if (now - lastHistoryUpdate.current >= HISTORY_THROTTLE_MS) {
                            lastHistoryUpdate.current = now;
                            setPriceHistory(prev => {
                                const next = { ...prev };
                                COINS.forEach(coin => {
                                    const p = newData[coin.id]?.usd || 0;
                                    if (p > 0) {
                                        next[coin.id] = [
                                            ...(next[coin.id] || []),
                                            { time: now, price: p },
                                        ].slice(-60);
                                    }
                                });
                                return next;
                            });
                        }
                    }
                } catch (error) {
                    console.error('[SSE] REST fallback fetch error:', error.message);
                }
            };

            fetchPrices();
            fallbackIntervalRef.current = setInterval(fetchPrices, 1500);
        };

        // --- Start SSE connection ---
        connectSSE();

        // --- Cleanup ---
        return () => {
            isMountedRef.current = false;
            if (fallbackIntervalRef.current) clearInterval(fallbackIntervalRef.current);
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
        };
    }, [setPrices, setPriceHistory]);
};

export default usePrices;
